<template>
    <div class="campaign-cell">
        <v-group
            __use-strict-mode
            data-qa="cell-group"
            data-type="cell-group"
            :x="cellX"
            :y="cellY"
            :draggable="isDraggable"
            :listening="!isDragging"
            @contextmenu="onContextMenu"
            @mouseenter="onMouseEnter"
            @mouseleave="onMouseExit"
            @dragstart="dragStart"
            @dragmove="dragMove"
            @dragend="dragEnd"
            @dblclick="onDoubleClick"
        >
            <v-group v-if="isNote" @click="onSingleClick">
                <v-group v-if="!isEditingName">
                    <v-rect v-if="(isDragging || !isNoteWithCustomStyle)" :config="configNotesRect" />

                    <KonvaRichNotes
                        data-qa="notes-cell"
                        :cell="cell"
                        :border-color="borderColor"
                        :flags-info="flagsInfo"
                        @on-click="onSingleClick"
                    />
                </v-group>
            </v-group>

            <KonvaCampaignCellBigCard
                v-else-if="displayBigCards"
                :is-sequence-view="isSequenceView"
                :cell="cell"
                :border-color="borderColor"
                :big-card-cells="bigCardCells"
                :enabled-integrations="enabledIntegrations"
                :lock-icon-config="configCellLockedIndicator"
                :lock-icon-src="imagePath.lockedSequence"
                @click="onSingleClick"
                @set-tooltip="setTooltip"
                @set-lock-tooltip="showLockTooltip"
                @set-hide-lock-tooltip="hideLockTooltip"
            />

            <v-group
                v-else-if="imageUrlFromCellType"
                @click="onSingleClick"
                @mouseenter="setTooltip"
            >
                <v-group>
                    <v-rect :config="configCellSquareContainer" />

                    <KonvaCampaignImage
                        :config="configCellIcon"
                        :src="imageUrlFromCellType"
                    />

                    <KonvaCampaignImage
                        v-if="statusIndicatorFromCell"
                        :src="statusIndicatorFromCell"
                        :config="configCellStatusIndicator"
                    />

                    <v-group
                        v-if="isLockedSequence && configCellLockedIndicator"
                        :x="configCellLockedIndicator.x"
                        :y="configCellLockedIndicator.y"
                        :listening="true"
                        @mouseenter="showLockTooltip"
                        @mouseleave="hideLockTooltip"
                    >
                        <KonvaCampaignImage
                            :config="{ ...configCellLockedIndicator, x: 0, y: 0 }"
                            :src="imagePath.lockedSequence"
                        />
                    </v-group>
                </v-group>

                <v-group v-if="shouldShowReportingView && reportingContainerConfig" :x="35" :y="-5">
                    <v-rect :config="reportingContainerConfig" />

                    <v-text :config="reportingTextConfig" />
                </v-group>
            </v-group>

            <v-rect v-else :config="configCellRectangle" />

            <v-text
                v-if="showText"
                data-qa="cell-label"
                :config="configCellText"
                @click="onClickName"
            />

            <v-group
                v-if="isHovering && !isNote"
                :config="configHoverMenuContainer"
                @mouseenter="onMouseEnterHoverEdge"
                @mouseleave="onMouseLeaveHoverEdge"
            >
                <KonvaCampaignImage
                    v-if="!isDragging && isHoveringAddEdge"
                    :config="configHoverAddEdge"
                    :src="cellConnectorSource"
                    @dragstart="dragEdge"
                    @dragend="dropEdge"
                    @click="onImageClick"
                />

                <KonvaCampaignImage
                    v-else
                    :config="configHoverAddEdge"
                    :src="cellConnectorSource"
                />
            </v-group>

            <KonvaCampaignCellTooltip
                v-show="displayTooltip"
                :tool-tip-parameters="tooltipParameters"
            />

            <KonvaCampaignCellTooltip
                v-show="displayLockTooltip"
                :tool-tip-parameters="lockTooltipParameters"
            />
        </v-group>

        <v-group v-if="isDragging && (ghostStatesEnabled || isCopying)" :config="configDragContainer">
            <v-group v-if="imageUrlFromCellType">
                <v-rect :config="configCellSquareContainer" />

                <KonvaCampaignImage
                    v-if="!isNote"
                    :config="configCellIcon"
                    :src="imageUrlFromCellType"
                    is-dragging
                />
            </v-group>

            <v-rect v-else :config="configCellRectangle" />

            <v-text v-if="showText" :config="configCellText" />
        </v-group>
    </div>
</template>

<script>
import KonvaCampaignImage from './KonvaCampaignImage.vue';
import KonvaCampaignCellTooltip from './KonvaCampaignCellTooltip.vue';
import KonvaRichNotes from './KonvaRichNotes.vue';
import KonvaCampaignCellBigCard from './KonvaCampaignCellBigCard.vue';

import { isParentCellLocked } from '../utils/cell/automation-cell-utils';
import { findOutgoingCells } from '../utils/cell/automation-edge-utils';

import {
    DECISION_TYPE,
    EDGE_VALIDATION_INVALID_STATES,
    EDGE_VALIDATION_STATE,
    FLOW_TYPE,
    MAIN_CANVAS_ID,
    SEQUENCE_START,
    NOTES_TYPE,
    INTEGRATION_TRIGGER_TYPE,
    INTEGRATION_ACTION_TYPE,
    MOUSE_CLICK,
    GRID_SIZE,
    CELL_TOOLTIP,
    ICON_TOOLTIP,
    BIG_CARD_WIDTH,
    SEQUENCE_START_WIDTH,
    PRESET_FILTERS,
    TIMERS,
    PROCESS_APPLY_REMOVE_TAG,
    NOTE_COLOR,
    GOALS,
    CELL_TYPE,
    GOAL_EMAIL_CONFIRM,
    BIG_CARD_PADDING,
} from '../utils/editor.constants';
import {
    getBigCardElementHeight,
    getIconPathByCellType,
    setCursor,
} from '../utils/cell/automation-display-utils';

const BLACK = '#000';
const BLUE = '#0080FF';
const WHITE = '#fff';
const PURPLE = '#8358f1';
const RED = '#e02500';
const GREEN = '#36A635';
const TRANSPARENT = 'transparent';
const STATUS_ICON_WIDTH = 16;
const STATUS_ICON_HEIGHT = 16;
const STATUS_ICON_BIG_CARD_SIZE = 24;

export default {
    components: {
        KonvaCampaignImage,
        KonvaCampaignCellTooltip,
        KonvaCampaignCellBigCard,
        KonvaRichNotes,
    },


    props: {
        shouldShowBigCard: {
            type: Boolean,
            default: false,
        },
        enabledIntegrations: {
            type: Array,
            default: () => [],
        },
        isSequenceView: {
            type: Boolean,
            default: false,
        },
        isEditingName: {
            type: Boolean,
            default: false,
        },
        isCopying: {
            type: Boolean,
            default: false,
        },
        ghostStatesEnabled: {
            type: Boolean,
            default: false,
        },
        isDragging: {
            type: Boolean,
            default: false,
        },
        isDraggable: {
            type: Boolean,
            default: true,
        },
        attachingEdgeFrom: {
            type: String,
            default: null,
        },
        selectedCells: {
            type: Array,
            required: true,
        },
        cell: {
            type: Object,
            required: true,
        },
        cells: {
            type: Array,
            default: () => ([]),
        },
        isHovering: {
            type: Boolean,
            default: false,
        },
        shouldShowLabels: {
            type: Boolean,
            default: false,
        },
        selectedSequence: {
            type: String,
            default: '1',
        },
        hasBeenPublished: {
            type: Boolean,
            default: false,
        },
        draggedEdgeValidState: {
            type: String,
            default: EDGE_VALIDATION_STATE.NONE,
        },
        inlineAddSources: {
            type: Array,
            default: () => [],
        },
        betaFeaturesEnabled: {
            type: Boolean,
            required: true,
        },

        // reporting-view props
        shouldShowReportingView: {
            type: Boolean,
            default: false,
        },
        cellReportingData: {
            type: Object,
            default: () => ({}),
        },
        preset: {
            type: String,
            required: true,
        },
        flagsInfo: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: [
        'on-click-cell',
        'on-click-name',
        'on-double-click-cell',
        'open-between-add',
        'on-mouse-enter',
        'on-mouse-exit',
        'on-drag-edge',
        'on-drop-edge',
        'drag-start',
        'drag-move',
        'drag-end',
    ],
    data() {
        return {
            isHoveringAddEdge: false,
            tooltipParameters: {},
            lockTooltipParameters: {},
            isHoveringLockIcon: false,
            imagePath: {
                connector: '/resources/funnel/images/svg/connectors/default.svg',
                connectorHover: '/resources/funnel/images/svg/connectors/hover.svg',
                connectorAddInline: '/resources/funnel/images/svg/connectors/add.svg',
                lockedSequence: '/resources/funnel/images/svg/lock.svg',
                redStatusIndicator: '/resources/funnel/images/svg/cell-icon/status-indicator/red.svg',
                yellowStatusIndicator: '/resources/funnel/images/svg/cell-icon/status-indicator/yellow.svg',
                questionMarkIcon: '/resources/funnel/images/svg/cell-icon/red/question.svg',
            },
        };
    },

    computed: {
        cellX() {
            return !this.cell.mxGeometry?.x || Number.isNaN(this.cell.mxGeometry.x) ? 0 : this.cell.mxGeometry.x;
        },

        cellY() {
            return !this.cell.mxGeometry?.y || Number.isNaN(this.cell.mxGeometry.y) ? 0 : this.cell.mxGeometry.y;
        },

        isConnectedToDiamond() {
            if (this.cell.type === DECISION_TYPE) return true;

            const targetCells = findOutgoingCells(this.cell.id, this.cells);

            return targetCells.some((cell) => cell.type === DECISION_TYPE);
        },

        isCellLocked() {
            return this.isLockedSequence || isParentCellLocked(this.cell, this.cells);
        },

        cellConnectorSource() {
            return this.addBetweenAllowed ? this.imagePath.connectorAddInline : this.imagePath.connector;
        },

        addBetweenAllowed() {
            const notInAddSources = !this.inlineAddSources.some((item) => item.id === this.cell.id);

            if (this.addElementsBetweenEnabled && notInAddSources && !this.isConnectedToDiamond && !this.isCellLocked) {
                return true;
            }

            return false;
        },

        addElementsBetweenEnabled() {
            return this.flagsInfo.addElementsBetweenEnabled && this.bigCardInlineAddEnabled;
        },

        bigCardInlineAddEnabled() {
            return this.flagsInfo.bigCardInlineAdd && this.betaFeaturesEnabled;
        },

        bigCardCells() {
            return this.cell?.elementChildren.length
                ? this.cell.elementChildren
                : [this.cell];
        },

        reportingContainerConfig() {
            if ((this.preset === PRESET_FILTERS.CURRENT && !(this.isSequence || this.isTimer)) || this.isDecisionDiamond) return null;

            const width = Math.max(16, this.cellReportingDataDisplay.length * 10);

            return {
                fill: this.isSequence || this.isPartOfSequence ? BLUE : GREEN,
                cornerRadius: 4,
                height: 14,
                width,
            };
        },

        cellReportingDataDisplay() {
            if (!this.shouldShowReportingView || !this.cellReportingData || this.isDecisionDiamond) return '';

            const {
                funnelStats = 0,
                activeStatistics = 0,
                activeQueueStatistics = 0,
                waitingQueueStatistics,
            } = this.cellReportingData;

            if (this.preset === PRESET_FILTERS.CURRENT) {
                return waitingQueueStatistics !== undefined ? `${waitingQueueStatistics}` : `${activeStatistics}/${activeQueueStatistics}`;
            }

            return `${funnelStats}`;
        },

        reportingTextConfig() {
            return {
                ...this.reportingContainerConfig,
                fill: WHITE,
                height: 14,
                y: 2,
                text: this.cellReportingDataDisplay,
                align: 'center',
                fontFamily: 'Sul Sans',
                fontSize: 10,
                fontStyle: 'bold',
            };
        },

        displayTooltip() {
            return Boolean(this.tooltipParameters?.toolTipText);
        },

        displayLockTooltip() {
            return Boolean(this.lockTooltipParameters?.toolTipText);
        },

        isBroken() {
            return this.cell?.value.broken === '1';
        },

        isReady() {
            return this.cell?.value?.ready === '1';
        },

        isPublished() {
            return this.cell?.value?.published === '1';
        },

        cellType() {
            return this.cell.type;
        },

        borderColor() {
            const { isSelected, isDragging, draggedEdgeValidState } = this;

            const showValidEdge = draggedEdgeValidState === EDGE_VALIDATION_STATE.VALID;
            const showInvalidEdge = EDGE_VALIDATION_INVALID_STATES.includes(draggedEdgeValidState);

            if (showValidEdge) return BLACK;
            if ((isSelected && !isDragging)) return PURPLE;
            if (showInvalidEdge) return RED;

            return TRANSPARENT;
        },

        configHoverMenuContainer() {
            return {
                width: 0,
                x: this.displayBigCards && this.isSequenceStart
                    ? BIG_CARD_WIDTH - SEQUENCE_START_WIDTH
                    : this.displayBigCards ? BIG_CARD_WIDTH : GRID_SIZE,
                y: 8,
            };
        },

        configHoverAddEdge() {
            return {
                visible: !this.attachingEdgeFrom,
                draggable: true,
                x: 0,
                y: 0,
            };
        },

        configCellIcon() {
            if (this.isDecisionDiamond) {
                return {
                    width: GRID_SIZE,
                    height: GRID_SIZE,
                    listening: false,
                };
            }

            return {
                x: GRID_SIZE / 4,
                y: GRID_SIZE / 4,
                width: GRID_SIZE / 2,
                height: GRID_SIZE / 2,
                listening: true,
            };
        },

        configCellStatusIndicator() {
            return {
                x: 36,
                y: -4,
                width: STATUS_ICON_WIDTH,
                height: STATUS_ICON_HEIGHT,
                listening: false,
            };
        },

        isWhen() {
            return [...Object.values(GOALS), INTEGRATION_TRIGGER_TYPE].includes(this.cell.type);
        },

        configCellLockedIndicator() {
            const fromCell = this.cell;
            const outgoingCells = findOutgoingCells(fromCell.meta.id, this.cells);

            if (!outgoingCells.length) return null;

            const toCell = outgoingCells[0];

            if (fromCell.type !== FLOW_TYPE || toCell.type !== GOAL_EMAIL_CONFIRM) return null;

            if (!this.displayBigCards) {
                const fromX = fromCell.mxGeometry?.x;
                const fromY = fromCell.mxGeometry?.y;
                const toX = toCell.mxGeometry?.x;
                const toY = toCell.mxGeometry?.y;

                const midX = ((fromX + toX) / 2) - fromX;
                const midY = ((fromY + toY) / 2) - fromY + STATUS_ICON_WIDTH;

                return {
                    x: midX,
                    y: midY,
                    width: STATUS_ICON_WIDTH,
                    height: STATUS_ICON_HEIGHT,
                    listening: true,
                };
            }

            const fromX = fromCell.mxGeometry.x + BIG_CARD_WIDTH;
            const fromY = fromCell.mxGeometry.y + BIG_CARD_PADDING;

            const toX = toCell.mxGeometry.x;
            const toY = toCell.mxGeometry.y + BIG_CARD_PADDING;

            const positionOffset = this.getOffsetBasedOnRelativePosition(fromCell.mxGeometry, toCell.mxGeometry);

            const centerEdgeX = (fromX + toX) / 2;
            const centerEdgeY = (fromY + toY) / 2;

            const size = STATUS_ICON_BIG_CARD_SIZE;

            const relativeX = centerEdgeX - fromCell.mxGeometry.x;
            const relativeY = centerEdgeY - fromCell.mxGeometry.y;

            return {
                x: relativeX - size / 2 + positionOffset.x,
                y: relativeY + positionOffset.y,
                width: size,
                height: size,
                listening: true,
            };
        },

        configDragContainer() {
            return {
                x: this.cell.mxGeometry.x,
                y: this.cell.mxGeometry.y,
                opacity: this.ghostStatesEnabled ? 0.0 : 1,
            };
        },

        configNotesRect() {
            const { cell, isHovering } = this;

            if (isHovering) {
                return {
                    width: cell.mxGeometry.width,
                    height: cell.mxGeometry.height,
                    fill: this.flagsInfo.addToolbarToNotesEnabled ? (cell.value.notesColor || NOTE_COLOR.gray) : '#fef8e4',
                    cornerRadius: 10,
                    shadowColor: 'black',
                    shadowBlur: 7,
                    shadowOffset: { x: 3, y: 3 },
                    shadowOpacity: 0.5,
                    strokeWidth: 0.8,
                    stroke: '#e0dac9',
                };
            }

            return {
                width: cell.mxGeometry.width,
                height: cell.mxGeometry.height,
                fill: this.flagsInfo.addToolbarToNotesEnabled ? (cell.value.notesColor || NOTE_COLOR.gray) : '#fef8e4',
                cornerRadius: 10,
                strokeWidth: 0.8,
                stroke: '#e0dac9',
            };
        },

        configCellSquareContainer() {
            let { width, height } = this.cell.mxGeometry;

            if (!this.isNote) {
                width = GRID_SIZE;
                height = GRID_SIZE;
            }

            if (this.displayBigCards && !this.isNote) {
                width = BIG_CARD_WIDTH;
                height = getBigCardElementHeight(this.bigCardCells, this.isSequenceView, this.isWhen);
            }

            return {
                fill: this.isDecisionDiamond
                    ? TRANSPARENT
                    : WHITE,
                shadowColor: 'black',
                stroke: this.borderColor,
                strokeWidth: 3,
                shadowOffset: { x: 0, y: 2 },
                shadowOpacity: 0.5,
                cornerRadius: 12,
                width,
                height,
                shadowForStrokeEnabled: false,
                perfectDrawEnabled: false,
            };
        },

        configCellRectangle() {
            return {
                stroke: '#555',
                strokeWidth: 2,
                fill: WHITE,
                cornerRadius: 5,
                width: this.cell.mxGeometry.width,
                height: this.cell.mxGeometry.height,
                perfectDrawEnabled: false,
                listening: false,
                shadowForStrokeEnabled: false,
            };
        },

        configCellText() {
            const textLabel = this.cellName;

            return {
                text: textLabel,
                width: 200,
                x: -76,
                y: 65,
                fill: BLACK,
                align: 'center',
                fontSize: 14,
                fontFamily: 'Proxima Nova',
            };
        },

        cellName() {
            const cellName = this.cell.value.name?.replaceAll('~br~', '\n');

            if (this.isApplyOrRemoveTag) {
                return this.applyOrRemoveTagLabel;
            }

            if (cellName) {
                return cellName;
            }

            return '';
        },

        applyOrRemoveTagLabel() {
            const { isApply, tagIds } = this.cell.value;

            if (!isApply) {
                return this.$t('funnel.edit.tag.new');
            }

            const applyOrRemove = (isApply === 'true') ? 'funnel.edit.tag.apply' : 'funnel.edit.tag.remove';
            const numTags = tagIds ? tagIds.length : 0;

            return this.$tc(applyOrRemove, numTags, { numTags });
        },

        isPartOfSequence() {
            return this.selectedSequence !== '1';
        },

        isSequenceStart() {
            return this.cell.type === SEQUENCE_START;
        },

        isSequence() {
            return this.cellType === FLOW_TYPE;
        },

        isTimer() {
            return Object.values(TIMERS).includes(this.cellType);
        },

        isLockedSequence() {
            return this.isSequence
                    && this.isMainCanvas
                    && this.cell?.value?.methodLocked === '1';
        },

        isMainCanvas() {
            return this.selectedSequence === MAIN_CANVAS_ID;
        },

        isSelected() {
            return this.selectedCells.map((cell) => cell.meta.id).includes(this.cell.meta.id);
        },

        isDecisionDiamond() {
            return this.cellType === DECISION_TYPE;
        },

        isApplyOrRemoveTag() {
            return this.cellType === PROCESS_APPLY_REMOVE_TAG;
        },

        isNote() {
            return this.cellType === NOTES_TYPE;
        },

        isNoteWithCustomStyle() {
            return this.isNote && this.cell.value.notes?.includes('<img') || this.cell.value.notes?.includes('<svg') || this.cell.value.notes?.includes(' style=');
        },

        statusIndicatorFromCell() {
            if (this.cellType === SEQUENCE_START) return null;

            if (this.isReady && !this.isBroken) {
                if (this.isPublished) return null;

                return this.imagePath.yellowStatusIndicator;
            }

            return this.imagePath.redStatusIndicator;
        },

        imageUrlFromCellType() {
            let iconColor;

            if (this.cellType === INTEGRATION_TRIGGER_TYPE) {
                return this.enabledIntegrations.find((integration) => {
                    return this.cell.value.integrationTriggerId === integration.id;
                })?.iconUrl || this.imagePath.questionMarkIcon;
            }

            if (this.cellType === INTEGRATION_ACTION_TYPE) {
                return this.enabledIntegrations.find((integration) => {
                    return this.cell.value.integrationActionId === integration.id;
                })?.iconUrl || this.imagePath.questionMarkIcon;
            }

            if (this.isBroken) iconColor = 'red';
            else if (this.cellType !== NOTES_TYPE) {
                if (this.isSequence || this.isDecisionDiamond || this.isPartOfSequence) iconColor = 'blue';
                else iconColor = 'green';
            } else iconColor = 'neutral';

            return getIconPathByCellType(this.cellType, iconColor);
        },

        showText() {
            const {
                cell,
                shouldShowLabels,
                isEditingName,
                displayBigCards,
            } = this;

            return shouldShowLabels && (cell.value.name || this.isApplyOrRemoveTag) && !isEditingName && !displayBigCards;
        },

        displayBigCards() {
            return this.shouldShowBigCard && !this.shouldShowReportingView;
        },
    },

    methods: {
        onContextMenu(event) {
            event.evt.preventDefault();
        },

        onClickName(event) {
            this.$emit('on-click-name', { cell: this.cell, event });
        },

        onSingleClick(event) {
            if (event.evt.which === MOUSE_CLICK.LEFT) {
                this.$emit('on-click-cell', { cell: { ...this.cell, cellReportingData: this.cellReportingData }, event });
            }
        },

        setTooltip() {
            if (this.isHoveringLockIcon) return;

            if (this.isBroken) {
                this.tooltipParameters = { toolTipText: CELL_TOOLTIP.elementBroken };
            } else if (this.hasBeenPublished) {
                if (this.isPublished) {
                    this.tooltipParameters = { toolTipText: CELL_TOOLTIP.elementPublished };
                } else {
                    this.tooltipParameters = { toolTipText: CELL_TOOLTIP.elementPublishedWithChanges };
                }
            } else if (this.isReady) {
                this.tooltipParameters = { toolTipText: CELL_TOOLTIP.elementReady };
            } else {
                this.tooltipParameters = { toolTipText: CELL_TOOLTIP.elementSetupRequired };
            }
        },

        hideTooltip() {
            this.tooltipParameters = {};
        },

        lockTooltipPosition() {
            const indicator = this.configCellLockedIndicator;

            if (!indicator) return { x: 0, y: 0 };

            return {
                x: indicator.x,
                y: indicator.y,
            };
        },

        showLockTooltip() {
            this.isHoveringLockIcon = true;

            const { x, y } = this.lockTooltipPosition();

            this.lockTooltipParameters = {
                toolTipText: this.$t(ICON_TOOLTIP.elementGetEmailOptIn),
                offsetX: x,
                offsetY: y,
                isIcon: true,
            };
        },

        hideLockTooltip() {
            this.isHoveringLockIcon = false;
            this.lockTooltipParameters = {};
        },

        onMouseEnterHoverEdge(event) {
            if (this.shouldShowReportingView) return;

            setCursor(event, 'pointer');
            this.hideTooltip();
            this.isHoveringAddEdge = true;

            const toolTipText = this.addBetweenAllowed ? CELL_TOOLTIP.addBetween : CELL_TOOLTIP.edgeCreation;
            const offsetY = this.addBetweenAllowed && this.displayBigCards ? 0 : 8;

            let offsetX = this.addBetweenAllowed && this.displayBigCards ? 196 : 36;

            offsetX -= this.addBetweenAllowed && this.displayBigCards && this.isSequenceStart ? 72 : 0;

            this.tooltipParameters = { toolTipText, offsetX, offsetY };
        },

        onMouseLeaveHoverEdge() {
            this.hideTooltip();
            this.isHoveringAddEdge = false;
        },

        onMouseEnter(event) {
            this.$emit('on-mouse-enter', event);
        },

        onMouseExit(event) {
            setCursor(event, 'default');
            this.isHoveringAddEdge = false;
            this.hideTooltip();
            this.$emit('on-mouse-exit', event);
        },

        onDoubleClick(event) {
            if (event.evt.which === MOUSE_CLICK.LEFT) {
                this.$emit('on-double-click-cell', this.cell);
            }
        },

        onImageClick(event) {
            if (this.addBetweenAllowed) {
                const data = {
                    sourceElement: this.cell,
                    type: this.isSequence || this.isSequenceView ? CELL_TYPE.THEN : CELL_TYPE.WHEN,
                    isSequenceView: this.isSequenceView,
                    event,
                };

                this.$emit('open-between-add', data);
            }
        },

        dragEdge(event) {
            this.hideTooltip();
            setCursor(event, 'dragging');
            this.$emit('on-drag-edge', { event, cell: this.cell });
        },

        dropEdge(event) {
            this.isHoveringAddEdge = false;
            setCursor(event, 'default');
            this.$emit('on-drop-edge', { event, cell: this.cell });
        },

        dragStart(event) {
            if (this.shouldShowReportingView || event.target.attrs['data-type'] !== 'cell-group') return;
            this.hideTooltip();
            setCursor(event, event.evt.ctrlKey ? 'copy' : 'grabbing');
            this.$emit('drag-start', { event, cell: this.cell });
        },

        dragMove(event) {
            this.$emit('drag-move', event);
        },

        dragEnd(event) {
            if (this.shouldShowReportingView || event.target.attrs['data-type'] !== 'cell-group') return;
            setCursor(event, 'pointer');
            this.$emit('drag-end', event);
        },

        comparePositions(referencePosition, position) {
            const compare = (a, b) => {
                if (a < b) return 1;
                if (a > b) return -1;

                return 0;
            };

            return {
                x: compare(referencePosition.x, position.x),
                y: compare(referencePosition.y, position.y),
            };
        },

        getOffsetBasedOnRelativePosition(referencePosition, position) {
            const response = { x: 0, y: 0 };
            const relativePosition = this.comparePositions(referencePosition, position);

            if (relativePosition.y !== 0) {
                response.x = -STATUS_ICON_WIDTH;
            }

            return response;
        },
    },
};
</script>
